import Table from '@/components/Table/Table';
import {
  ColumnDef,
  TableData,
} from '@/components/Table/TableHelpers';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import TrashIcon from '@/assets/trash.svg?react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchPartners, postMarketsDetach } from '@/lib/api';
import { partnerQueryKeys } from '@/lib/partnerQueryKeys';
import TableActions from './TableActions';
import { useState } from 'react';
import { MarketSchema, PartnerSchema } from '@/lib/types';
import useHasAnyUserRole from '@/hooks/useHasAnyUserRole';
import Markets from '@/constants/Markets';

type Data = {
  id: number;
  market: string;
  relatedBrands: string[];
};

const MarketTable = ({
  selectedPartnerId,
  onAddMarketClick,
  markets,
  onMarketDetach,
  loading = false,
}: {
  selectedPartnerId: string | null;
  onAddMarketClick: () => void;
  markets: MarketSchema[];
  onMarketDetach: () => void;
  loading?: boolean;
}) => {
  const [searchText, setSearchText] = useState('');
  const hasUserRole = useHasAnyUserRole([
    'Super Admin',
    'Internal Admin',
  ]);
  const { data } = useQuery({
    queryKey: partnerQueryKeys.detail(selectedPartnerId),
    queryFn: () =>
      fetchPartners({ partner_ids: [Number(selectedPartnerId)] }),
  });
  const partner = data?.find?.(
    (d: PartnerSchema) => d.id === Number(selectedPartnerId),
  );
  const canManageMarkets = hasUserRole && partner?.type === 'lead';

  const { mutate } = useMutation({
    mutationFn: ({
      market_id,
      partner_id,
    }: {
      market_id: number;
      partner_id: string;
    }) => postMarketsDetach(market_id, partner_id),
    onSuccess: onMarketDetach,
  });

  const handleBrandsChange = (id: number, value: TableData) => {
    console.log(`Brands changed for ${id} - ${value}`);
  };

  const columns: ColumnDef<Data>[] = [
    {
      field: 'market',
      label: 'Market',
      width: '20%',
    },
    {
      field: 'relatedBrands',
      label: 'Related Brands',
      editable: false,
      onChange: handleBrandsChange,
    },
    ...(canManageMarkets
      ? [
          {
            id: 'actions',
            label: 'Delete',
            align: 'right' as const,
            sortable: false,
            render: (row: Data) => (
              <IconButton
                onClick={() => {
                  if (!selectedPartnerId) return;

                  mutate({
                    partner_id: selectedPartnerId,
                    market_id: row.id,
                  });
                }}
              >
                <TrashIcon />
              </IconButton>
            ),
          },
        ]
      : []),
  ];

  const rows = (markets || [])
    .map(({ id, name }) => ({
      id,
      market: Markets[name]?.text || name,
      relatedBrands: [],
    }))
    .filter((r) =>
      r.market.toLowerCase().includes(searchText.toLowerCase()),
    );

  return (
    <>
      <TableActions
        addLabel={canManageMarkets ? 'Add Market' : undefined}
        onAddClick={canManageMarkets ? onAddMarketClick : undefined}
        searchText={searchText}
        onSearchTextChange={setSearchText}
      />
      <StyledTable rows={rows} columns={columns} loading={loading} />
    </>
  );
};

const StyledTable = styled(Table<Data>)({
  padding: '0 40px',
});

export default MarketTable;
